/**
 * EDP-00001 — Enterprise Experience Architecture (frontend registry)
 * Single source of truth for navigation groups, IA, and workspace standards.
 */

export const EDP_ID = 'EDP-00001';

export const DESIGN_PHILOSOPHY = [
  { id: 'less_navigation', label: 'Less Navigation', description: 'Reduce nav depth; surface context over menus' },
  { id: 'more_context', label: 'More Context', description: 'Show where, why, and what-next inline' },
  { id: 'ai_first', label: 'AI First', description: 'Every major workspace offers AI insight or next-step' },
  { id: 'workspace_first', label: 'Workspace First', description: 'Consistent workspace model across modules' },
  { id: 'explain_before_execute', label: 'Explain Before Execute', description: 'Preview and explain consequential actions' },
  { id: 'zero_hidden_logic', label: 'Zero Hidden Logic', description: 'No silent AI actions; audit trail visible' },
  { id: 'enterprise_scale', label: 'Enterprise Scale', description: 'Supports agencies, teams, and multi-brand orgs' },
  { id: 'human_control', label: 'Human Control', description: 'Humans approve consequential automation' },
  { id: 'progressive_disclosure', label: 'Progressive Disclosure', description: 'Summary first; detail on demand' },
  { id: 'keyboard_first', label: 'Keyboard First', description: 'All flows completable without mouse' },
  { id: 'mobile_aware', label: 'Mobile Aware', description: 'Approvals and monitoring on mobile' },
  { id: 'accessibility_first', label: 'Accessibility First', description: 'WCAG 2.2 AA minimum everywhere' },
] as const;

export const PERSONAS = [
  { id: 'amazon_brand_owner', label: 'Amazon Brand Owner', focus: 'Revenue, brand health, strategy' },
  { id: 'listing_manager', label: 'Listing Manager', focus: 'Catalog, content, SEO' },
  { id: 'advertising_manager', label: 'Advertising Manager', focus: 'Campaigns, bids, ROAS' },
  { id: 'operations_manager', label: 'Operations Manager', focus: 'Orders, FBA, fulfillment' },
  { id: 'finance_manager', label: 'Finance Manager', focus: 'Settlements, fees, cash flow' },
  { id: 'inventory_manager', label: 'Inventory Manager', focus: 'Stock, replenishment, aged inventory' },
  { id: 'agency', label: 'Agency', focus: 'Multi-client portfolio management' },
  { id: 'enterprise_team', label: 'Enterprise Team', focus: 'Cross-functional coordination' },
  { id: 'designer', label: 'Designer', focus: 'Creative assets, brand store' },
  { id: 'content_creator', label: 'Content Creator', focus: 'Listings, A+, reviews' },
  { id: 'support_team', label: 'Support Team', focus: 'Returns, VoC, post-sales' },
  { id: 'administrator', label: 'Administrator', focus: 'Users, integrations, governance' },
  { id: 'developer', label: 'Developer', focus: 'APIs, extensions, diagnostics' },
  { id: 'auditor', label: 'Auditor', focus: 'Compliance, audit trails' },
] as const;

export const IA_HIERARCHY = [
  'Platform',
  'Organization',
  'Workspace',
  'Marketplace',
  'Brand',
  'Portfolio',
  'Products',
  'Operations',
  'Analytics',
  'Settings',
] as const;

export const NAV_GROUPS = [
  'Home',
  'Commerce',
  'Growth',
  'Intelligence',
  'Finance',
  'Brand & Strategy',
  'AI & Automation',
  'Platform',
] as const;

export const WORKSPACE_ZONES = [
  'Overview',
  'Explorer',
  'Details',
  'AI Assistant',
  'Insights',
  'Recommendations',
  'Timeline',
  'History',
  'Actions',
  'Related Objects',
  'Audit',
] as const;

export const WORKFLOW_PHASES = [
  'Understand',
  'Analyze',
  'Recommend',
  'Explain',
  'Simulate',
  'Approve',
  'Execute',
  'Measure',
  'Learn',
] as const;

export const AIX_MODES = [
  'Global Assistant',
  'Inline AI',
  'Floating AI',
  'Context AI',
  'Bulk AI',
  'Selection AI',
  'Explain AI',
  'Simulation AI',
  'Approval AI',
  'Learning AI',
] as const;

export const DASHBOARD_TYPES = [
  'Executive Dashboard',
  'Operational Dashboard',
  'Analytical Dashboard',
  'Personal Dashboard',
  'Department Dashboard',
  'AI Dashboard',
] as const;

export const NOTIFICATION_CATEGORIES = [
  'Critical Alerts',
  'Recommendations',
  'Approvals',
  'Tasks',
  'Mentions',
  'AI Suggestions',
  'System Messages',
  'Marketplace Events',
] as const;
